/* DealCommandPage.jsx — Calm operational Deal Room.
 *
 * Per-deal operational workspace for progressing a leasehold property
 * transaction toward exchange. Replaces the prior dense panel-stack
 * with a single calm canvas:
 *
 *   Header (property image + address + leasehold badge + status)
 *   Tabs (Overview · Timeline · Updates · Documents)
 *   Three-column body (Overview tab):
 *     Left  — Transaction Timeline (vertical milestones)
 *     Centre — Transaction Updates (operational feed + Log Update)
 *     Right — Target Exchange · Chain · Outstanding · Parties · Next Step · Documents
 *
 * Doctrine: Resolve > Display · Reduce > Add · Calm > Stimulate.
 * Tone reinforcement: every surface should answer the question
 * "what moves this transaction one step closer to exchange?"
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Share2, Bell, MoreHorizontal, Building2, ChevronRight,
  Check, Circle, ArrowRight, Calendar, Link as LinkIcon, AlertCircle,
  Phone, MessageSquare, Pencil, FileText, FileCheck, ClipboardList,
  Search as SearchIcon, Mail, Info, Filter,
} from "lucide-react";
import { useAuth } from "../data/authStore";
import { useChainStore } from "../data/chainStore";
import {
  fullJourneyFor,
  fullJourneyIndexFor,
  optsForDeal,
} from "../data/transactionJourney";
import { dealStatusFor } from "../data/dealHealth";
import { api } from "../lib/api";
import { useOperationalVisibility } from "../hooks/useOperationalVisibility";
import { useDealActivity, invalidateDealActivity } from "../hooks/useDealActivity";
import { useOperationalSignals } from "../hooks/useOperationalSignals";
import { computeExchangeReadiness } from "../lib/exchangeReadiness";
import { ExchangeReadinessCard } from "../components/operational/ExchangeReadinessCard";
import { ExchangeWeekProtocol, isExchangeWeekActive } from "../components/operational/ExchangeWeekProtocol";
import { UndoToast } from "../components/operational/UndoToast";
import { ChainRibbon } from "../components/operational/ChainRibbon";
import {
  SellerConfidencePill,
  SolicitorResponsivenessPill,
} from "../components/operational/SignalPills";
import {
  VISIBILITY_KINDS,
  visibilityLinesFromActivity,
} from "../constants/operationalMomentum";
import { OperationalVisibilityList } from "../components/OperationalVisibilityList";

// ─── Operator-facing 5-stage progression ─────────────────────────
// The leasehold milestone list (9 entries) lives in the timeline.
// This is the high-level Linear-grade chip rail that gives every
// operator instant clarity on the transaction bucket. Stays calm
// and never aggressive — single thin rail, soft current emphasis.
const STAGE_BUCKETS = [
  { id: "under_offer",  label: "Under Offer"  },
  { id: "aml",          label: "AML"          },
  { id: "conveyancing", label: "Conveyancing" },
  { id: "pre_exchange", label: "Pre-Exchange" },
  { id: "exchange",     label: "Exchange"     },
];

const STAGE_TO_BUCKET_IDX = {
  offer:          0,
  memo:           0,
  aml:            1,
  solicitors:     2,
  mortgage_app:   2,
  survey:         2,
  searches:       2,
  enquiries:      2,
  mortgage_offer: 3,
  exchange_ready: 3,
  exchanged:      4,
  completed:      4,
};

// ─── Leasehold milestone taxonomy ─────────────────────────────────
//
// iter-292 — Single source of truth.
//
// The local LEASEHOLD_MILESTONES array + STAGE_TO_MILESTONE_IDX map
// were removed. The Deal Command Page's vertical "Transaction
// Timeline" now consumes the canonical journey module so every
// timeline surface (rail, vertical timeline, sales progression,
// canonical journey strip) shares the same node ids, ordering,
// labels and tenure gating.
//
// `transactionMilestonesFor(deal)` returns the full ordered list
// (pre-journey + journey) with each entry shaped as
// `{ id, label, longLabel, party }` so the existing MilestoneRow
// renderer renders unchanged.
function transactionMilestonesFor(deal) {
  const tenure = String(deal?.tenure || "leasehold").toLowerCase();
  const opts = optsForDeal(deal);
  return fullJourneyFor(tenure, opts).map((n) => ({
    id: n.id,
    label: n.longLabel,        // full milestone name on the vertical timeline
    party: n.party,
    pre: !!n.pre,
  }));
}

function transactionMilestoneIndexFor(deal) {
  const tenure = String(deal?.tenure || "leasehold").toLowerCase();
  const opts = optsForDeal(deal);
  return fullJourneyIndexFor(tenure, deal?.stage, opts);
}

// ─── Milestone progression actions ───────────────────────────────
// One calm, deterministic next-step action per active milestone.
// On click → log a transparent activity row + advance the deal
// stage. Readiness + protocol + timeline + What Changed Today
// all recompute from the new stage/activity automatically.
//
// IMPORTANT: this is NOT a CRM edit form. The agent is recording
// the operational event that just happened in the transaction,
// not configuring anything. Each `advanceTo` target is the next
// canonical stage in `chainStore.DEAL_STAGES` so progression
// never regresses.
//
// iter-292 — Rekeyed on canonical journey node ids (mortgage ·
// searches · survey · contract · bsa · mgmt_pack · enquiries ·
// report · exchange · complete) so the action menu lives at the
// same node identity as every other timeline surface. Pre-journey
// nodes (offer / memo / aml) also carry actions for the early
// stages of the deal.
const MILESTONE_ACTION = {
  offer: {
    label: "Mark Memo Issued",
    activity_type: "milestone_completed",
    title: "Memorandum of sale issued",
    advanceTo: "memo",
  },
  memo: {
    label: "Mark AML Cleared",
    activity_type: "milestone_completed",
    title: "AML verification complete",
    advanceTo: "aml",
  },
  aml: {
    label: "Mark Mortgage Applied",
    activity_type: "milestone_completed",
    title: "Mortgage application submitted",
    advanceTo: "mortgage_app",
  },
  mortgage: {
    label: "Mark Mortgage Offer Received",
    activity_type: "milestone_completed",
    title: "Mortgage offer received",
    advanceTo: "mortgage_offer",
  },
  searches: {
    label: "Mark Searches Returned",
    activity_type: "document_upload",
    title: "Searches returned",
    advanceTo: "searches",
  },
  survey: {
    label: "Mark Survey Returned",
    activity_type: "document_upload",
    title: "Survey returned",
    advanceTo: "survey",
  },
  contract: {
    label: "Mark Contract Pack Received",
    activity_type: "document_upload",
    title: "Draft contract pack received",
    advanceTo: "contract_pack",
  },
  bsa: {
    label: "Mark BSA Review Complete",
    activity_type: "milestone_completed",
    title: "BSA documentation reviewed by buyer's solicitor",
    advanceTo: "bsa",
  },
  mgmt_pack: {
    label: "Mark Pack Received",
    activity_type: "document_upload",
    title: "Leasehold management pack received",
    advanceTo: "mgmt_pack",
  },
  enquiries: {
    label: "Mark Enquiries Resolved",
    activity_type: "milestone_completed",
    title: "Final enquiries resolved",
    advanceTo: "enquiries",
  },
  report: {
    label: "Mark Report Issued",
    activity_type: "milestone_completed",
    title: "Report on title issued",
    advanceTo: "report_on_title",
  },
  exchange: {
    label: "Mark Exchanged",
    activity_type: "stage_change",
    title: "Contracts exchanged",
    advanceTo: "exchanged",
  },
  complete: {
    label: "Mark Completed",
    activity_type: "milestone_completed",
    title: "Sale completed",
    advanceTo: "completed",
  },
};

const STATUS_TONE = {
  "Moving Well":         { bg: "#ECFDF5", fg: "#047857", dot: "#10B981" },
  "Requires Attention":  { bg: "#FFFBEB", fg: "#B45309", dot: "#F59E0B" },
  "Progress Slowing":    { bg: "#FEF2F2", fg: "#B91C1C", dot: "#EF4444" },
};

const UPDATE_KIND_ICON = {
  document:        FileText,
  doc:             FileText,
  document_upload: FileText,
  survey:          Calendar,
  homebuyer:       Calendar,
  searches:        SearchIcon,
  enquiries:       Mail,
  contract:        FileCheck,
  mortgage:        ClipboardList,
  leasehold:       Building2,
  milestone:       Check,
  blocker:         AlertCircle,
  note:            Info,
  default:         FileText,
};

const UPDATE_KIND_TINT = {
  document:        { bg: "#ECFDF5", fg: "#047857" },
  doc:             { bg: "#ECFDF5", fg: "#047857" },
  document_upload: { bg: "#ECFDF5", fg: "#047857" },
  survey:          { bg: "#F5F3FF", fg: "#6D28D9" },
  homebuyer:       { bg: "#F5F3FF", fg: "#6D28D9" },
  searches:        { bg: "#FFFBEB", fg: "#B45309" },
  enquiries:       { bg: "#FFF7ED", fg: "#C2410C" },
  contract:        { bg: "#EFF6FF", fg: "#1D4ED8" },
  mortgage:        { bg: "#EFF6FF", fg: "#1D4ED8" },
  leasehold:       { bg: "#F5F3FF", fg: "#6D28D9" },
  milestone:       { bg: "#ECFDF5", fg: "#047857" },
  blocker:         { bg: "#FFFBEB", fg: "#B45309" },
  note:            { bg: "#F1F5F9", fg: "#475569" },
  default:         { bg: "#F1F5F9", fg: "#475569" },
};

// Kind → left accent border colour. Renders a 3px tint that gives
// each operational update a calm visual category without resorting
// to badges or chips. NULL means no left accent (most updates).
// Milestones and blockers are intentionally elevated for scan.
const UPDATE_KIND_ACCENT = {
  milestone: "#10B981",
  contract:  "#1D4ED8",
  mortgage:  "#1D4ED8",
  blocker:   "#F59E0B",
};

// Short category label rendered at the top-right of each update so
// operators can scan the activity feed by category at a glance.
const UPDATE_KIND_LABEL = {
  document:   "Document",
  survey:     "Survey",
  searches:   "Searches",
  enquiries:  "Enquiries",
  contract:   "Contract",
  mortgage:   "Mortgage",
  leasehold:  "Leasehold",
  milestone:  "Milestone",
  blocker:    "Awaiting",
  note:       "Note",
};

// ─── Utilities ────────────────────────────────────────────────────

const fmtDateUK = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const fmtTime = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
};

const daysFromNow = (ts) => {
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : Math.round((d.getTime() - Date.now()) / 86_400_000);
};

const initials = (s) =>
  String(s || "?").trim().split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase();

const classifyKind = (kind) => {
  const k = String(kind || "").toLowerCase();
  if (k.includes("milestone") || k.includes("exchanged") || k.includes("completion") || k.includes("memorand")) return "milestone";
  if (k.includes("blocker") || k.includes("awaiting") || k.includes("stale") || k.includes("delayed")) return "blocker";
  if (k.includes("doc") || k.includes("upload")) return "document";
  if (k.includes("survey") || k.includes("valuation") || k.includes("homebuyer")) return "survey";
  if (k.includes("search"))     return "searches";
  if (k.includes("enquir"))     return "enquiries";
  if (k.includes("contract"))   return "contract";
  if (k.includes("mortgage"))   return "mortgage";
  if (k.includes("leasehold") || k.includes("management pack")) return "leasehold";
  if (k.includes("note"))       return "note";
  return "default";
};

// Relative time helper — "42 mins ago" / "3 hrs ago" / "2 days ago".
// Calm operational tone; falls back to absolute date once > 14 days.
const relativeAgo = (ts) => {
  if (!ts) return "";
  const then = new Date(ts).getTime();
  if (!Number.isFinite(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60_000);
  if (mins < 1)   return "just now";
  if (mins < 60)  return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs} hr${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "yesterday";
  if (days < 14)  return `${days} days ago`;
  return new Date(ts).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

// ─── Page ─────────────────────────────────────────────────────────

export default function DealCommandPage() {
  const { dealId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { deals, findProperty, findContact, addActivity, updateDeal } = useChainStore();

  const effectiveId = dealId || deals.find((d) => !d.archived)?.id;
  const deal = useMemo(() => deals.find((d) => d.id === effectiveId), [deals, effectiveId]);

  const [tab, setTab] = useState("overview");
  const [documents, setDocuments] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [logOpen, setLogOpen] = useState(false);
  const [actionPending, setActionPending] = useState(false);
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);
  // Calm Undo toast state — only ever ONE active at a time.
  // Holds the operational receipt needed to reverse the last
  // milestone action: the milestone label that was marked, the
  // newly-created activity id (to delete), and the previous
  // stage (to restore).
  const [undoState, setUndoState] = useState(null);
  // { label, activityId, previousStage } | null

  useEffect(() => {
    if (!effectiveId) return;
    api.get(`/api/deals/${effectiveId}/documents`).then((r) => setDocuments(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get(`/api/deals/${effectiveId}/participants-enriched`).then((r) => setParticipants(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [effectiveId]);

  // Lifted to parent so Exchange-Week Protocol + Right Rail share
  // one fetch (the hook also caches per-deal at module level).
  // Must be called BEFORE any early return — rules of hooks.
  const liveActivity = useDealActivity(effectiveId, { refreshKey: activityRefreshKey });

  // Embedded operational signals (P2.1 + P2.2). Single fetch shared by
  // HeroCard + PartiesCard via the in-memory cache inside the hook.
  const { data: opSignals } = useOperationalSignals(effectiveId, { refreshKey: activityRefreshKey });

  // Calm operational handler: log an activity row + advance the
  // deal stage in one go. Both writes are intentionally
  // independent so a partial failure (e.g., advance succeeds,
  // activity blip) still surfaces visible progression. After the
  // writes we invalidate the activity cache and bump the refresh
  // key so the readiness card + Exchange-Week Protocol pick up
  // the new ground truth on the next render.
  const onMilestoneAction = async (milestoneId) => {
    const cfg = MILESTONE_ACTION[milestoneId];
    if (!cfg || !deal || actionPending) return;
    setActionPending(true);
    const previousStage = deal.stage;
    let activityId = null;
    try {
      const created = await addActivity(deal.id, {
        activity_type: cfg.activity_type,
        title: cfg.title,
      });
      activityId = created?.id || null;
      if (cfg.advanceTo && deal.stage !== cfg.advanceTo) {
        await updateDeal(deal.id, { stage: cfg.advanceTo });
      }
      invalidateDealActivity(deal.id);
      setActivityRefreshKey((k) => k + 1);
      // Open the calm Undo toast — auto-dismisses in 5s.
      setUndoState({
        label: `${cfg.title} · marked`,
        activityId,
        previousStage,
        advancedTo: cfg.advanceTo,
      });
    } catch (_e) {
      // Calm degradation — never alarm the operator if the write
      // bounces. The next user action retries naturally.
    } finally {
      setActionPending(false);
    }
  };

  // Calm true rollback: delete the activity row + revert the
  // stage to its pre-action value. Cache invalidated so the
  // readiness card / Exchange-Week Protocol / Operational
  // Visibility / What Changed Today all rewind.
  const onUndoMilestone = async () => {
    if (!undoState || !deal) return;
    const snapshot = undoState;
    setUndoState(null);
    try {
      if (snapshot.activityId) {
        await api.delete(`/api/v2/deals/${deal.id}/activity/${snapshot.activityId}`).catch(() => {});
      }
      if (snapshot.previousStage && snapshot.previousStage !== deal.stage) {
        await updateDeal(deal.id, { stage: snapshot.previousStage });
      }
      invalidateDealActivity(deal.id);
      setActivityRefreshKey((k) => k + 1);
    } catch (_e) {
      // Calm: silent recovery, the operator can re-mark if needed.
    }
  };

  if (!deal) {
    return (
      <div className="min-h-screen" style={{ background: "#F8F8FB" }}>
        <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out min-h-screen flex items-center justify-center px-6" data-testid="dealroom-empty">
          <div className="text-center">
            <p className="text-[14px] text-slate-500">Deal not found.</p>
            <Link to="/deals" className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-violet-700">
              <ArrowLeft size={13} /> Back to Deals
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const prop  = findProperty(deal.propertyId);
  const buyer  = findContact(deal.buyerId);
  const seller = findContact(deal.sellerId);
  // iter-292 — Driven off the canonical transaction journey so the
  // vertical timeline matches every other surface.
  const milestones = transactionMilestonesFor(deal);
  const currentIdx = transactionMilestoneIndexFor(deal);
  const currentMilestone = milestones[Math.min(currentIdx, milestones.length - 1)] || milestones[0];
  const targetExchangeDate = fmtDateUK(deal.targetExchange);
  const daysToExchange     = deal.targetExchange ? daysFromNow(deal.targetExchange) : null;

  /* iter-294 — Canonical deal-health resolver.
   *
   * The Deal Command hero pill reads the same band as every
   * other surface. The legacy local heuristic (chainHealth < 60
   * → "Progress Slowing", < 80 → "Requires Attention") was
   * retired; the band lives in `dealHealth.dealStatusFor()` and
   * the hero re-labels it with its own operator-facing copy
   * (Moving Well / Requires Attention / Progress Slowing) via
   * `label.hero` — no UI change. */
  const _statusCanon = dealStatusFor(deal);
  const status = _statusCanon.label.hero;
  const tone = STATUS_TONE[status];

  return (
    <div className="min-h-screen" style={{ background: "#F8F8FB" }}>
      <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out min-h-screen overflow-x-hidden" data-testid="dealroom-main">
        <div className="px-4 md:px-10 py-7 md:py-9 max-w-[1480px] mx-auto w-full cf-page-enter">
          <Breadcrumb />

          <HeroCard
            prop={prop} deal={deal} status={status} tone={tone}
            stageLabel={currentMilestone?.label || ""}
            targetExchangeDate={targetExchangeDate}
            daysToExchange={daysToExchange}
            user={user}
            sellerSignal={opSignals?.seller_confidence}
          />

          {isExchangeWeekActive(daysToExchange) && (
            <ExchangeWeekProtocol
              deal={deal}
              daysToExchange={daysToExchange}
              targetExchangeDate={targetExchangeDate}
              activity={liveActivity || []}
              testid="dealroom-exchange-week-protocol"
            />
          )}

          <div className="mt-5">
            <ChainRibbon
              deal={deal}
              allDeals={deals}
              title="Chain"
              testid="dealroom-chain-ribbon"
            />
          </div>

          <ProgressionConfidenceBand
            status={status}
            stageLabel={currentMilestone?.label || ""}
            daysToExchange={daysToExchange}
          />

          <StageProgressionRail
            currentBucketIdx={STAGE_TO_BUCKET_IDX[deal.stage] ?? 0}
            status={status}
          />

          <Tabs tab={tab} setTab={setTab} />

          <div className="grid grid-cols-1 xl:grid-cols-[260px_minmax(0,1fr)_320px] gap-6 md:gap-7 mt-6">
            <TransactionTimeline milestones={milestones} currentIdx={currentIdx} deal={deal} onMilestoneAction={onMilestoneAction} actionPending={actionPending} />

            {tab === "documents" ? (
              <DealDocumentsPanel documents={documents} />
            ) : (
              <TransactionUpdates
                deal={deal} prop={prop}
                onLogUpdate={() => setLogOpen(true)}
              />
            )}

            <RightRail
              deal={deal}
              targetExchangeDate={targetExchangeDate}
              daysToExchange={daysToExchange}
              participants={participants}
              buyer={buyer} seller={seller}
              negotiatorName={user?.full_name || user?.name || "James Parker"}
              documents={documents}
              activity={liveActivity}
              opSignals={opSignals}
            />
          </div>
        </div>
      </main>

      {logOpen && (
        <LogUpdateModal
          open={logOpen}
          onClose={() => setLogOpen(false)}
          onSubmit={async (payload) => {
            try {
              await addActivity(deal.id, payload);
            } finally {
              setLogOpen(false);
            }
          }}
        />
      )}

      <UndoToast
        open={!!undoState}
        label={undoState?.label || ""}
        onUndo={onUndoMilestone}
        onDismiss={() => setUndoState(null)}
        testid="dealroom-undo-toast"
      />
    </div>
  );
}

// ─── Breadcrumb ──────────────────────────────────────────────────

function Breadcrumb() {
  return (
    <nav className="flex items-center gap-1.5 text-[12.5px] text-slate-500 pl-12 md:pl-0 mb-4" data-testid="dealroom-breadcrumb">
      <Link to="/deals" className="hover:text-slate-900">Deals</Link>
      <ChevronRight size={11} className="text-slate-300" />
      <span className="text-slate-900 font-semibold">Deal Room</span>
    </nav>
  );
}

// ─── Hero card ───────────────────────────────────────────────────

function HeroCard({ prop, deal, status, tone, stageLabel, targetExchangeDate, daysToExchange, user, sellerSignal }) {
  const negName = user?.full_name || user?.name || "James Parker";
  return (
    <section
      className="rounded-2xl bg-white border border-slate-100 p-5 md:p-6 flex items-stretch gap-5 flex-col md:flex-row"
      style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.02)" }}
      data-testid="dealroom-hero"
    >
      <div className="relative w-full md:w-[200px] h-[140px] md:h-auto rounded-xl overflow-hidden bg-slate-100 shrink-0">
        {prop?.image || prop?.thumb ? (
          <img src={prop.image || prop.thumb} alt={prop.address} className="w-full h-full object-cover" />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center text-[18px] font-bold text-violet-700"
            style={{ background: "linear-gradient(135deg, rgba(139,92,246,0.10), rgba(99,102,241,0.05))" }}
          >
            {initials(prop?.address || deal.name)}
          </div>
        )}
        <span
          className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/90 text-[10px] font-bold text-slate-700"
          style={{ backdropFilter: "blur(6px)" }}
          data-testid="dealroom-leasehold-badge"
        >
          <Building2 size={11} /> Leasehold
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <h1 className="text-[20px] md:text-[24px] font-bold text-slate-900 tracking-[-0.02em] leading-tight truncate" data-testid="dealroom-address">
          {prop?.address || deal.name}
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-2.5">
          <span
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10.5px] font-bold"
            style={{ background: tone.bg, color: tone.fg, border: `1px solid ${tone.dot}33` }}
            data-testid="dealroom-status"
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: tone.dot }} />
            {status}
          </span>
          <span className="text-[12px] text-slate-500" data-testid="dealroom-tenure-badge">
            {(() => {
              const t = (deal.tenure || prop?.tenure || "").toString().toLowerCase();
              if (t === "freehold")  return "Sale (Freehold)";
              if (t === "leasehold") return "Sale (Leasehold)";
              return "Sale";
            })()}
          </span>
          {sellerSignal && sellerSignal.state !== "Stable" && (
            <SellerConfidencePill
              state={sellerSignal.state}
              reason={sellerSignal.reasons?.[0] || null}
              size="xs"
              testid="dealroom-seller-confidence-pill"
            />
          )}
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3">
          <Meta label="Stage" value={stageLabel} testid="dealroom-meta-stage" />
          <Meta
            label="Target Exchange"
            value={
              <span className="inline-flex items-baseline gap-1.5">
                <span className="tabular-nums">{targetExchangeDate || "TBC"}</span>
                {daysToExchange != null && (
                  <span className="text-[11px]" style={{ color: daysToExchange < 0 ? "#B91C1C" : daysToExchange <= 14 ? "#B45309" : "#94A3B8" }}>
                    {daysToExchange < 0 ? `(${Math.abs(daysToExchange)}d overdue)` : daysToExchange === 0 ? "(today)" : `(in ${daysToExchange} days)`}
                  </span>
                )}
              </span>
            }
            testid="dealroom-meta-target"
          />
          <Meta
            label="Negotiator"
            value={
              <span className="inline-flex items-center gap-2">
                <span
                  className="w-6 h-6 rounded-full inline-flex items-center justify-center text-[9.5px] font-bold shrink-0"
                  style={{ background: "rgba(139,92,246,0.10)", color: "#6D28D9" }}
                >
                  {initials(negName)}
                </span>
                {negName}
              </span>
            }
            testid="dealroom-meta-negotiator"
          />
        </div>
      </div>

      <div className="flex md:flex-col items-end justify-between gap-2.5 shrink-0">
        <button
          type="button"
          className="h-9 px-3 rounded-xl bg-white border border-slate-100 text-[12.5px] font-semibold text-slate-700 inline-flex items-center gap-2 hover:bg-slate-50"
          data-testid="dealroom-share"
        >
          <Share2 size={13} /> Share Deal Room
        </button>
        <button
          type="button"
          className="w-9 h-9 rounded-xl border border-slate-100 inline-flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-50"
          aria-label="More"
          data-testid="dealroom-more"
        >
          <MoreHorizontal size={14} />
        </button>
      </div>
    </section>
  );
}

function Meta({ label, value, testid }) {
  return (
    <div className="min-w-0" data-testid={testid}>
      <p className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-slate-400 mb-1">{label}</p>
      <p className="text-[13px] font-semibold text-slate-900 truncate">{value}</p>
    </div>
  );
}

// ─── Tabs ────────────────────────────────────────────────────────

const TABS = [
  { id: "overview",  label: "Overview" },
  { id: "timeline",  label: "Timeline" },
  { id: "updates",   label: "Updates" },
  { id: "documents", label: "Documents" },
];

// ─── Progression Confidence Band ──────────────────────────────────
// A single calm row beneath the hero that subtly reassures the
// operator (or any internal viewer) that the transaction is
// progressing. Not a dashboard — just one sentence + a tone.
function ProgressionConfidenceBand({ status, stageLabel, daysToExchange }) {
  const tone =
    status === "Progress Slowing"    ? { ring: "rgba(239,68,68,0.18)",  dot: "#DC2626", text: "#991B1B" } :
    status === "Requires Attention"  ? { ring: "rgba(245,158,11,0.20)", dot: "#D97706", text: "#92400E" } :
                                       { ring: "rgba(16,185,129,0.20)", dot: "#059669", text: "#047857" };

  const sentence =
    status === "Progress Slowing"    ? "Resolving a blocker — chain support engaged."
    : status === "Requires Attention" ? "Coordinating with solicitors to keep momentum."
    :                                   "Progressing steadily toward exchange.";

  const exchangeHint =
    daysToExchange != null && daysToExchange > 0
      ? ` · ${daysToExchange} day${daysToExchange === 1 ? "" : "s"} to target exchange`
      : "";

  return (
    <div
      className="mt-4 inline-flex items-center gap-2.5 px-3.5 py-2 rounded-full bg-white"
      style={{ border: `1px solid ${tone.ring}` }}
      data-testid="dealroom-progression-confidence"
    >
      <span
        aria-hidden
        className="w-1.5 h-1.5 rounded-full cf-pulse-soft shrink-0"
        style={{ background: tone.dot }}
      />
      <p className="text-[12px] font-semibold tracking-tight" style={{ color: tone.text }}>
        {sentence}
      </p>
      <p className="text-[11.5px] text-slate-500 hidden sm:block">
        Currently · <span className="text-slate-700 font-semibold">{stageLabel}</span>
        <span className="text-slate-400">{exchangeHint}</span>
      </p>
    </div>
  );
}

// ─── Stage Progression Rail ──────────────────────────────────────
// Calm 5-stage operator-facing rail. Single source of truth for
// "where is this transaction right now?". Past = filled slate dot,
// current = soft violet dot + pulse, future = muted ring. Thin
// connector lines bind the dots into one continuous rail — no
// oversized progress bars, no CRM-pipeline energy.
function StageProgressionRail({ currentBucketIdx, status }) {
  const isUrgent = status === "Progress Slowing";
  const isAttention = status === "Requires Attention";
  const currentTone = isUrgent
    ? { bg: "rgba(239,68,68,0.10)", border: "#DC2626", dot: "#DC2626", label: "#991B1B" }
    : isAttention
    ? { bg: "rgba(245,158,11,0.12)", border: "#D97706", dot: "#D97706", label: "#92400E" }
    : { bg: "rgba(99,102,241,0.10)", border: "#6366F1", dot: "#6366F1", label: "#4338CA" };

  return (
    <nav
      className="mt-4 rounded-2xl bg-white border border-slate-100 px-4 py-3.5 md:px-5"
      style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.02)" }}
      data-testid="dealroom-stage-rail"
      aria-label="Transaction stage progression"
    >
      <div className="flex items-center gap-1.5 md:gap-2 overflow-x-auto -mx-1 px-1 cf-scroll">
        {STAGE_BUCKETS.map((b, i) => {
          const isPast = i < currentBucketIdx;
          const isCurrent = i === currentBucketIdx;
          const isLast = i === STAGE_BUCKETS.length - 1;
          const dotBg = isPast
            ? "#0F172A"
            : isCurrent
            ? currentTone.dot
            : "#FFFFFF";
          const dotBorder = isPast
            ? "#0F172A"
            : isCurrent
            ? currentTone.border
            : "#E2E8F0";
          const labelTone = isPast
            ? "text-slate-700"
            : isCurrent
            ? "font-semibold"
            : "text-slate-400";
          const connectorTone = i < currentBucketIdx ? "#0F172A" : "#E2E8F0";
          return (
            <React.Fragment key={b.id}>
              <div
                className="inline-flex items-center gap-2 shrink-0 px-1"
                data-testid={`stage-bucket-${b.id}`}
                data-current={isCurrent || undefined}
              >
                <span
                  className={`relative w-2.5 h-2.5 rounded-full inline-flex items-center justify-center ${
                    isCurrent ? "cf-pulse-soft" : ""
                  }`}
                  style={{
                    background: dotBg,
                    border: `1.5px solid ${dotBorder}`,
                  }}
                  aria-hidden
                />
                <span
                  className={`text-[12px] tracking-tight whitespace-nowrap ${labelTone}`}
                  style={isCurrent ? { color: currentTone.label } : undefined}
                >
                  {b.label}
                </span>
              </div>
              {!isLast && (
                <span
                  aria-hidden
                  className="h-px flex-1 min-w-[12px] md:min-w-[24px]"
                  style={{ background: connectorTone }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </nav>
  );
}


function Tabs({ tab, setTab }) {
  return (
    <nav
      className="mt-5 flex items-center gap-1 border-b border-slate-100 overflow-x-auto"
      data-testid="dealroom-tabs"
    >
      {TABS.map((t) => {
        const active = tab === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`relative px-3 py-3 text-[13px] font-semibold whitespace-nowrap transition ${
              active ? "text-violet-700" : "text-slate-500 hover:text-slate-900"
            }`}
            data-testid={`dealroom-tab-${t.id}`}
          >
            {t.label}
            {active && (
              <span className="absolute left-0 right-0 bottom-[-1px] h-[1.5px] rounded-full bg-violet-500" style={{ transition: "all 240ms cubic-bezier(0.22,1,0.36,1)" }} />
            )}
          </button>
        );
      })}
    </nav>
  );
}

// ─── Transaction Timeline (LEFT column) ─────────────────────────

function TransactionTimeline({ milestones, currentIdx, deal, onMilestoneAction, actionPending }) {
  return (
    <aside className="space-y-6 min-w-0" data-testid="dealroom-timeline">
      <section
        className="rounded-2xl bg-white border border-slate-100 p-5 cf-card-hover"
        style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.02)" }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-[14px] font-bold text-slate-900 tracking-tight">Transaction Timeline</h3>
          <Link
            to="#"
            className="text-[11px] font-semibold text-slate-500 hover:text-violet-700 transition-colors"
            data-testid="dealroom-timeline-view-full"
          >
            View full timeline
          </Link>
        </div>

        <ol className="relative">
          <span aria-hidden="true" className="absolute left-[10px] top-2 bottom-2 w-px bg-slate-100" />
          {milestones.map((m, i) => {
            const isPast    = i < currentIdx;
            const isCurrent = i === currentIdx;
            const action    = isCurrent ? MILESTONE_ACTION[m.id] : null;
            return (
              <MilestoneRow
                key={m.id}
                milestoneId={m.id}
                label={m.label}
                dateOrSub={
                  isPast    ? milestoneDateFor(i, deal, milestones.length) :
                  isCurrent ? "In Progress" :
                  "Pending"
                }
                state={isPast ? "past" : isCurrent ? "current" : "future"}
                renderChildren={isCurrent && m.id === "survey" ? () => <SurveyChildren /> : undefined}
                action={action}
                onAction={onMilestoneAction}
                actionPending={actionPending}
              />
            );
          })}
        </ol>
      </section>

      <LeaseholdInfoCard />
    </aside>
  );
}

function milestoneDateFor(i, deal, totalMilestones) {
  // Calm deterministic past dates based on the deal's reference date.
  const base = deal.targetExchange ? new Date(deal.targetExchange) : new Date();
  const offsetDays = (i + 1) * 4; // 4-day spacing leading up to target
  const d = new Date(base.getTime() - (totalMilestones - i + 4) * 86_400_000 + offsetDays * 86_400_000);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function MilestoneRow({ milestoneId, label, dateOrSub, state, renderChildren, action, onAction, actionPending }) {
  const dotPalette = {
    past:    { bg: "#ECFDF5", border: "#10B981", icon: "#10B981" },
    current: { bg: "#F5F3FF", border: "#6D28D9", icon: "#6D28D9" },
    future:  { bg: "#FFFFFF", border: "#E2E8F0", icon: "#CBD5E1" },
  }[state];
  return (
    <li
      className={`relative pl-9 pb-7 last:pb-0 ${state === "current" ? "rounded-xl bg-violet-50/30 -ml-3 pl-[44px] py-3" : ""}`}
      data-testid={`milestone-${state}`}
    >
      <span
        className={`absolute left-0 top-0.5 w-[21px] h-[21px] rounded-full inline-flex items-center justify-center ${state === "current" ? "cf-pulse-soft" : ""}`}
        style={{ background: dotPalette.bg, border: `1.5px solid ${dotPalette.border}` }}
      >
        {state === "past"   ? <Check size={11} strokeWidth={3} color={dotPalette.icon} /> :
         state === "current"? <ArrowRight size={10} strokeWidth={3} color={dotPalette.icon} /> :
         <Circle size={5} fill={dotPalette.icon} color={dotPalette.icon} />}
      </span>
      <p className={`text-[13px] font-semibold ${state === "future" ? "text-slate-400" : "text-slate-900"} leading-tight`}>
        {label}
      </p>
      <p className={`text-[11.5px] mt-1 tabular-nums ${
        state === "current" ? "text-violet-700 font-semibold" :
        state === "past"    ? "text-slate-400" :
        "text-slate-500"
      }`}>
        {dateOrSub}
      </p>
      {renderChildren && (
        <div className="mt-3 space-y-2">{renderChildren()}</div>
      )}
      {state === "current" && action && (
        <button
          type="button"
          onClick={() => onAction?.(milestoneId)}
          disabled={actionPending}
          className="cf-press mt-2.5 inline-flex items-center gap-1.5 h-9 sm:h-7 px-3 sm:px-2.5 rounded-md text-[12px] sm:text-[11.5px] font-semibold text-violet-700 bg-white border border-violet-100 hover:border-violet-300 hover:bg-violet-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid={`milestone-action-${milestoneId}`}
          aria-label={`${action.label} for ${label}`}
        >
          {actionPending ? (
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block w-3 h-3 rounded-full border-[1.5px] border-violet-300 border-t-violet-700 animate-spin"
                aria-hidden
              />
              Recording…
            </span>
          ) : (
            <>
              <Check size={11} strokeWidth={2.5} />
              {action.label}
            </>
          )}
        </button>
      )}
    </li>
  );
}

function SurveyChildren() {
  const items = [
    { dot: "#10B981", title: "Mortgage valuation complete", date: "12 May 2025" },
    { dot: "#6D28D9", title: "Homebuyer survey booked",     date: "18 May 2025" },
  ];
  return items.map((it, i) => (
    <div key={i} className="flex items-start gap-2 pl-1">
      <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: it.dot }} />
      <div className="min-w-0">
        <p className="text-[12px] font-semibold text-slate-700 leading-tight">{it.title}</p>
        <p className="text-[11px] text-slate-500 tabular-nums">{it.date}</p>
      </div>
    </div>
  ));
}

function LeaseholdInfoCard() {
  return (
    <section
      className="rounded-2xl border p-4"
      style={{
        background: "rgba(99,102,241,0.025)",
        borderColor: "rgba(99,102,241,0.10)",
      }}
      data-testid="dealroom-leasehold-info"
    >
      <div className="flex items-start gap-2.5">
        <span className="w-7 h-7 rounded-lg inline-flex items-center justify-center shrink-0" style={{ background: "rgba(99,102,241,0.08)", color: "#6366F1" }}>
          <Info size={13} />
        </span>
        <div className="min-w-0">
          <p className="text-[12.5px] font-bold text-slate-900">Leasehold transaction</p>
          <p className="text-[11.5px] text-slate-500 mt-1 leading-relaxed">
            Additional steps apply for leasehold properties.
          </p>
          <a href="#" className="text-[11.5px] font-semibold text-violet-700 hover:text-violet-800 mt-2 inline-block">
            Learn more
          </a>
        </div>
      </div>
    </section>
  );
}

// ─── Transaction Updates (CENTRE column) ────────────────────────

function TransactionUpdates({ deal, prop, onLogUpdate }) {
  const updates = useMemo(() => {
    const seed = (deal.activity || []).slice(0, 25).map((a, i) => ({
      id:    a.id || `${deal.id}-${i}`,
      kind:  classifyKind(a.kind || a.activity_type),
      title: a.text || a.title || "Operational update",
      body:  a.body || a.text || "",
      by:    a.by   || a.actor || a.author || "",
      role:  a.actor_role || "",
      at:    a.at || a.created_at,
      tags:  a.tags || [],
    }));
    if (seed.length > 0) return seed;
    // Calm operational seed when the workspace has no recorded activity yet
    const refDate = deal.targetExchange ? new Date(deal.targetExchange) : new Date();
    const d = (offsetDays) => new Date(refDate.getTime() - offsetDays * 86_400_000).toISOString();
    return [
      { id: "u1", kind: "milestone", title: "Memorandum acknowledged",      body: "Both solicitors have acknowledged the memorandum of sale.",         by: "Whitmore Legal", role: "Seller solicitor", at: d(0.5) },
      { id: "u2", kind: "mortgage",  title: "Mortgage offer received",      body: "Lender has issued the formal mortgage offer to the buyer's solicitor.", by: "Connell Finance", role: "Broker",        at: d(2) },
      { id: "u3", kind: "document",  title: "Mortgage valuation received",  body: "The mortgage valuation has been received and approved.",            by: "Sarah",          role: "Lender",           at: d(18) },
      { id: "u4", kind: "survey",    title: "Homebuyer survey booked",      body: `Homebuyer survey booked for ${refDate.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}.`, by: "James Parker", role: "", at: d(18) },
      { id: "u5", kind: "leasehold", title: "Leasehold management pack received", body: `The management pack has been received from Riverside Blocks.`, by: "Lucy", role: "Managing Agent",   at: d(18), tags: ["LPE1", "Insurance", "3 years accounts", "Ground rent"] },
      { id: "u6", kind: "blocker",   title: "Awaiting buyer ID verification", body: "AML pack still requires the buyer's proof of address.",            by: "Helena Marsh",   role: "Buyer solicitor",  at: d(19) },
      { id: "u7", kind: "enquiries", title: "Enquiries raised",             body: "Buyer's solicitor has raised initial enquiries.",                   by: "Carter & Co",    role: "Solicitor",        at: d(19) },
      { id: "u8", kind: "searches",  title: "Searches returned",            body: "Local, water and environmental searches received.",                 by: "SearchFlow",     role: "",                 at: d(20) },
      { id: "u9", kind: "contract",  title: "Draft contract received",      body: "Draft contract received from seller's solicitor.",                  by: "Dawson Legal",   role: "Solicitor",        at: d(21) },
    ];
  }, [deal]);

  return (
    <section className="space-y-4 min-w-0" data-testid="dealroom-updates">
      <header
        className="flex items-center justify-between px-1"
      >
        <h2 className="font-display text-[15px] font-bold tracking-tight text-slate-900">Transaction Updates</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="h-8 px-3 rounded-lg text-[11.5px] font-semibold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5"
            data-testid="dealroom-updates-filter"
          >
            All updates <ChevronRight size={10} className="rotate-90" />
          </button>
          <button
            type="button"
            className="w-8 h-8 rounded-lg inline-flex items-center justify-center text-slate-400 hover:text-slate-900 hover:bg-slate-50"
            aria-label="More filters"
          >
            <Filter size={12} />
          </button>
        </div>
      </header>

      <ul className="space-y-3.5">
        {updates.map((u) => <UpdateCard key={u.id} u={u} />)}
      </ul>

      <LogUpdateCTA onClick={onLogUpdate} />
    </section>
  );
}

function UpdateCard({ u }) {
  const Icon       = UPDATE_KIND_ICON[u.kind] || UPDATE_KIND_ICON.default;
  const tint       = UPDATE_KIND_TINT[u.kind] || UPDATE_KIND_TINT.default;
  const accent     = UPDATE_KIND_ACCENT[u.kind] || null;
  const category   = UPDATE_KIND_LABEL[u.kind] || null;
  const isMilestone = u.kind === "milestone";
  return (
    <li
      className="relative rounded-2xl bg-white border border-slate-100 px-5 py-4 cf-card-hover"
      style={{
        // Calm 3px accent for milestone / blocker / contract / mortgage —
        // visual category cue without resorting to badges.
        borderLeftWidth: accent ? "3px" : undefined,
        borderLeftColor: accent || undefined,
      }}
      data-testid={`update-${u.id}`}
      data-update-kind={u.kind}
    >
      <div className="flex items-start gap-3.5 min-w-0">
        <span
          className="w-9 h-9 rounded-xl inline-flex items-center justify-center shrink-0"
          style={{ background: tint.bg, color: tint.fg }}
        >
          <Icon size={15} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className={`text-[13.5px] leading-snug text-slate-900 ${isMilestone ? "font-bold tracking-tight" : "font-semibold"}`}>
                {u.title}
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5">
                {category && <span className="font-semibold text-slate-500">{category}</span>}
                {category && " · "}
                <span className="tabular-nums">{relativeAgo(u.at)}</span>
              </p>
            </div>
            <p className="text-[10.5px] text-slate-300 tabular-nums shrink-0 text-right hidden sm:block">
              {fmtDateUK(u.at)}
              <br />
              <span>{fmtTime(u.at)}</span>
            </p>
          </div>
          {u.body && <p className="text-[12.5px] text-slate-600 mt-2 leading-relaxed">{u.body}</p>}
          {Array.isArray(u.tags) && u.tags.length > 0 && (
            <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-400 mr-0.5">Includes</span>
              {u.tags.map((t) => (
                <span key={t} className="inline-flex items-center px-2 py-0.5 rounded-md text-[10.5px] font-medium text-slate-600 bg-slate-50 border border-slate-200">
                  {t}
                </span>
              ))}
            </div>
          )}
          {u.by && (
            <p className="text-[11px] text-slate-500 mt-2.5">
              by <span className="font-medium text-slate-700">{u.by}</span>
              {u.role && <span className="text-slate-300"> · {u.role}</span>}
            </p>
          )}
        </div>
      </div>
    </li>
  );
}

function LogUpdateCTA({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-2xl bg-white border border-slate-100 px-5 py-4 flex items-center justify-between gap-4 hover:bg-slate-50 cf-card-hover cf-press text-left"
      data-testid="dealroom-log-update-cta"
    >
      <div className="flex items-start gap-3 min-w-0">
        <span className="w-9 h-9 rounded-xl inline-flex items-center justify-center shrink-0" style={{ background: "#F5F3FF", color: "#6D28D9" }}>
          <Pencil size={15} />
        </span>
        <div className="text-left min-w-0">
          <p className="text-[13.5px] font-bold text-slate-900">Log an update</p>
          <p className="text-[12px] text-slate-500 mt-0.5">Record a new update, share information or add a note to the timeline.</p>
        </div>
      </div>
      <span
        className="h-9 px-4 rounded-xl text-[12.5px] font-semibold text-slate-700 border border-slate-200 inline-flex items-center gap-1.5 shrink-0 bg-white"
      >
        Log Update
      </span>
    </button>
  );
}

// ─── Deal Documents Panel ────────────────────────────────────────
// Calm, scannable list of every document on the transaction. Grouped
// by folder (AML / Memo / Contracts / Other) to mirror how solicitors
// actually file material. Read-only on this surface — uploads, requests
// and downloads live on the Documents Hub. This panel exists so the
// "Documents" tab on the Deal Room is never a dead button.
function _humanBytes(n) {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

const _FOLDER_ORDER = ["AML", "Memo", "Contracts", "Mortgage", "Searches", "Leasehold", "Other"];

function DealDocumentsPanel({ documents }) {
  const docs = Array.isArray(documents) ? documents : [];
  const byFolder = useMemo(() => {
    const map = {};
    for (const d of docs) {
      const f = d.folder || "Other";
      (map[f] ||= []).push(d);
    }
    return map;
  }, [docs]);
  const folders = _FOLDER_ORDER.filter((f) => byFolder[f]);
  for (const k of Object.keys(byFolder)) {
    if (!_FOLDER_ORDER.includes(k)) folders.push(k);
  }

  return (
    <section className="space-y-4 min-w-0" data-testid="dealroom-tab-documents-panel">
      <header className="flex items-center justify-between px-1">
        <div>
          <h2 className="font-display text-[15px] font-bold tracking-tight text-slate-900">Documents</h2>
          <p className="text-[11.5px] text-slate-500 mt-0.5">
            {docs.length === 0
              ? "No documents on this transaction yet."
              : `${docs.length} document${docs.length === 1 ? "" : "s"} on this transaction. Manage uploads in the Documents Hub.`}
          </p>
        </div>
        <Link
          to="/documents"
          className="h-8 px-3 rounded-lg text-[11.5px] font-semibold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5"
          data-testid="dealroom-docs-open-hub"
        >
          Open Documents Hub <ChevronRight size={11} />
        </Link>
      </header>

      {docs.length === 0 ? (
        <div
          className="rounded-2xl bg-white border border-slate-100 p-8 text-center"
          style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.02)" }}
          data-testid="dealroom-docs-empty"
        >
          <FileText size={22} className="mx-auto text-slate-300" strokeWidth={1.6} />
          <p className="mt-3 text-[13px] font-semibold text-slate-700">Nothing filed yet</p>
          <p className="text-[11.5px] text-slate-500 mt-1">
            Documents uploaded against this transaction will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="dealroom-docs-list">
          {folders.map((folder) => {
            const items = byFolder[folder] || [];
            if (items.length === 0) return null;
            return (
              <div
                key={folder}
                className="rounded-2xl bg-white border border-slate-100 overflow-hidden"
                style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.02)" }}
                data-testid={`dealroom-docs-folder-${folder.toLowerCase()}`}
              >
                <div className="px-5 py-3 flex items-center gap-2 border-b border-slate-100">
                  <FileCheck size={13} className="text-violet-500" />
                  <span className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-700">{folder}</span>
                  <span className="text-[10.5px] font-semibold text-slate-400 tabular-nums ml-auto">
                    {items.length}
                  </span>
                </div>
                <ul>
                  {items.map((d) => (
                    <li
                      key={d.id || d.name}
                      className="flex items-center gap-3 px-5 py-3 border-t border-slate-50 first:border-t-0 hover:bg-slate-50/60 transition-colors"
                      data-testid={`dealroom-docs-row-${d.id || d.name}`}
                    >
                      <span
                        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                        style={{ background: "rgba(99,102,241,0.06)", color: "#6366F1" }}
                        aria-hidden="true"
                      >
                        <FileText size={13} strokeWidth={2.1} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[12.5px] font-semibold text-slate-900 truncate">{d.name}</p>
                        <p className="text-[10.5px] text-slate-500 tabular-nums mt-0.5">
                          {_humanBytes(d.size)}
                          {d.uploaded_by_name ? <> · {d.uploaded_by_name}</> : null}
                          {d.created_at ? <> · {new Date(d.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</> : null}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ─── Right Rail ──────────────────────────────────────────────────

function RightRail({ deal, targetExchangeDate, daysToExchange, participants, buyer, seller, negotiatorName, documents, activity, opSignals }) {
  const { filterLines } = useOperationalVisibility();
  // Hook always called (cached per-deal at module level). Prefer
  // the parent-lifted prop when provided.
  const fetched = useDealActivity(deal?.id);
  const liveActivity = activity ?? fetched;

  // Prefer the real Postgres `deal_activities` feed; fall back to
  // a calm stage-derived ledger only when no real activity exists.
  const raw = useMemo(() => {
    const fromReal = visibilityLinesFromActivity(liveActivity, { limit: 5 });
    if (fromReal.length > 0) return fromReal;
    return visibilityEventsFromDeal(deal);
  }, [liveActivity, deal]);
  const { lines, showTime } = filterLines(raw);

  // Deterministic readiness — derived from stage + real activity.
  const readiness = useMemo(
    () => computeExchangeReadiness(deal, liveActivity),
    [deal, liveActivity]
  );

  return (
    <aside className="space-y-6 xl:sticky xl:top-6 self-start min-w-0" data-testid="dealroom-right-rail">
      <TargetExchangeCard date={targetExchangeDate} days={daysToExchange} />
      <ExchangeReadinessCard readiness={readiness} testid="dealroom-exchange-readiness" />
      <ChainStatusCard />
      <OperationalVisibilityList
        lines={lines}
        showTime={showTime}
        title="Operational visibility"
        emptyText="No operational activity surfaced yet."
        testid="dealroom-operational-visibility"
      />
      <OutstandingItemsCard />
      <PartiesCard buyer={buyer} seller={seller} participants={participants} negotiatorName={negotiatorName} opSignals={opSignals} />
      <NextStepCard />
      <TransactionDocumentsCard documents={documents} deal={deal} />
    </aside>
  );
}

// Stage-aware composer — derives a calm operational visibility feed
// from the deal's current stage. Deterministic, no fabricated PII,
// no presence signals. Output already carries `kind` so the audience
// filter in `useOperationalVisibility().filterLines` handles
// role-sensitive softening (internal vs buyer/seller vs solicitor).
function visibilityEventsFromDeal(deal) {
  if (!deal) return [];
  const now = Date.now();
  const at = (daysAgo) => new Date(now - daysAgo * 86_400_000).toISOString();
  const stage = deal.stage || "offer";

  // Build a stage-ordered ledger of "what has already happened
  // operationally" up to and including the current stage. Each
  // ChainFlow demo stage emits 1-2 visibility events; the audience
  // filter decides what each viewer actually sees.
  const ledger = [];
  const order = [
    "offer", "aml", "memo",
    "solicitors", "mortgage_app", "survey",
    "searches", "enquiries",
    "mortgage_offer", "exchange_ready", "exchanged", "completed",
  ];
  const reachedIdx = Math.max(0, order.indexOf(stage));

  const STAGE_EVENTS = {
    offer:          [{ kind: VISIBILITY_KINDS.memorandum_issued }],
    aml:            [{ kind: VISIBILITY_KINDS.memorandum_acknowledged }],
    memo:           [{ kind: VISIBILITY_KINDS.memorandum_acknowledged }],
    solicitors:     [{ kind: VISIBILITY_KINDS.contract_pack_issued }],
    mortgage_app:   [],
    survey:         [{ kind: VISIBILITY_KINDS.document_uploaded }],
    searches:       [{ kind: VISIBILITY_KINDS.searches_returned }],
    enquiries:      [{ kind: VISIBILITY_KINDS.enquiries_raised },
                     { kind: VISIBILITY_KINDS.management_pack_received }],
    mortgage_offer: [{ kind: VISIBILITY_KINDS.mortgage_offer_received }],
    exchange_ready: [{ kind: VISIBILITY_KINDS.enquiries_acknowledged }],
    exchanged:      [{ kind: VISIBILITY_KINDS.milestone_completed }],
    completed:      [{ kind: VISIBILITY_KINDS.milestone_acknowledged }],
  };

  for (let i = 0; i <= reachedIdx; i++) {
    const stageKey = order[i];
    const evs = STAGE_EVENTS[stageKey] || [];
    // Spread events backward in time: most recent first, with the
    // current-stage events at "today".
    for (const ev of evs) {
      const daysAgo = (reachedIdx - i) * 3 + (ledger.length % 2);
      ledger.push({ ...ev, at: at(daysAgo) });
    }
  }

  // Surface most-recent first, cap at 5 — calm, never noisy.
  return ledger.reverse().slice(0, 5);
}

function RailCard({ title, badge, action, children, testid, className = "" }) {
  return (
    <section
      className={`rounded-2xl bg-white border border-slate-100 p-5 cf-card-hover ${className}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-3.5">
        <p className="text-[13px] font-bold text-slate-900 tracking-tight inline-flex items-center gap-2">
          {title}
          {badge != null && (
            <span
              className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-bold tabular-nums"
              style={{ background: "#F1F5F9", color: "#475569" }}
            >
              {badge}
            </span>
          )}
        </p>
        {action}
      </div>
      {children}
    </section>
  );
}

function TargetExchangeCard({ date, days }) {
  // Calendar icon tone mirrors the days-out indicator below — calm
  // by default, ambers in the final fortnight, never aggressive red
  // unless the date is already overdue.
  const iconTone =
    days != null && days < 0
      ? { bg: "#FEF2F2", fg: "#B91C1C" }
      : days != null && days <= 14
      ? { bg: "rgba(245,158,11,0.10)", fg: "#B45309" }
      : { bg: "rgba(99,102,241,0.08)", fg: "#4F46E5" };
  return (
    <RailCard
      title="Target Exchange"
      testid="rail-target-exchange"
      action={<span className="w-8 h-8 rounded-lg inline-flex items-center justify-center" style={{ background: iconTone.bg, color: iconTone.fg }}><Calendar size={13} /></span>}
    >
      <p className="text-[20px] font-bold text-slate-900 tabular-nums leading-none">{date || "TBC"}</p>
      {days != null && (
        <p className="text-[11.5px] font-semibold mt-2" style={{ color: days < 0 ? "#B91C1C" : days <= 14 ? "#B45309" : "#94A3B8" }}>
          {days < 0 ? `(${Math.abs(days)} days overdue)` : days === 0 ? "(today)" : `(in ${days} days)`}
        </p>
      )}
    </RailCard>
  );
}

function ChainStatusCard() {
  return (
    <RailCard
      title="Chain Status"
      action={<a href="#" className="text-[11px] font-semibold text-slate-500 hover:text-violet-700 transition-colors">View chain</a>}
      testid="rail-chain-status"
    >
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 rounded-xl inline-flex items-center justify-center" style={{ background: "rgba(139,92,246,0.10)", color: "#6D28D9" }}>
          <LinkIcon size={14} />
        </span>
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-slate-900">3 linked transactions</p>
          <p className="text-[11.5px] text-slate-500 mt-0.5">
            Chain status: <span className="text-emerald-600 font-semibold">Stable</span>
          </p>
        </div>
      </div>
    </RailCard>
  );
}

function OutstandingItemsCard() {
  const items = [
    { title: "Awaiting signed enquiries", sub: "Buyer's solicitor",        dot: "#F59E0B" },
    { title: "Mortgage offer to be issued", sub: "Lender",                 dot: "#F59E0B" },
  ];
  return (
    <RailCard title="Outstanding Items" badge={items.length} testid="rail-outstanding">
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li
            key={i}
            className="group flex items-start justify-between gap-3 -mx-2 px-2 py-1.5 rounded-lg hover:bg-slate-50/70 transition-colors"
            data-testid={`outstanding-${i}`}
          >
            <div className="flex items-start gap-2.5 min-w-0">
              <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: it.dot }} />
              <div className="min-w-0">
                <p className="text-[12.5px] font-semibold text-slate-900 leading-tight">{it.title}</p>
                <p className="text-[11.5px] text-slate-500 mt-0.5">{it.sub}</p>
              </div>
            </div>
            <ChevronRight size={13} className="text-slate-300 shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
          </li>
        ))}
      </ul>
    </RailCard>
  );
}

function PartiesCard({ buyer, seller, participants, negotiatorName, opSignals }) {
  // Embedded operational signals — solicitor responsiveness + seller
  // confidence rendered next to the relevant party row only when the
  // state is not the calm default. Mapping is by lowercase role label
  // so it works regardless of API vs deterministic seed source.
  const sigByRole = useMemo(() => {
    const map = new Map();
    for (const s of opSignals?.solicitors || []) {
      if (s.role) map.set(s.role.toLowerCase(), s.signal);
    }
    return map;
  }, [opSignals]);
  const sellerSig = opSignals?.seller_confidence;

  // Calm deterministic party list when participant endpoint returns empty
  const partyList = useMemo(() => {
    const fromApi = (participants || []).slice(0, 6).map((p) => ({
      name: p.full_name || p.name || "—",
      role: p.role_label || p.role || "",
      roleKey: String(p.role || "").toLowerCase(),
    }));
    if (fromApi.length > 0) return fromApi;

    return [
      { name: negotiatorName, role: "Sales Negotiator", you: true, roleKey: "agent" },
      { name: seller?.name || "Anna Smith",  role: "Seller", roleKey: "seller" },
      { name: buyer?.name  || "Tom & Emily Brown", role: "Buyer", roleKey: "buyer" },
      { name: "Carter & Co Solicitors",       role: "Buyer's Solicitor", roleKey: "buyer_solicitor" },
      { name: "Dawson Legal",                 role: "Seller's Solicitor", roleKey: "seller_solicitor" },
      { name: "Sarah Johnson",                role: "Mortgage Broker", roleKey: "broker" },
    ];
  }, [participants, buyer, seller, negotiatorName]);

  return (
    <RailCard
      title="Parties"
      action={<a href="#" className="text-[11px] font-semibold text-slate-500 hover:text-violet-700 transition-colors">View all</a>}
      testid="rail-parties"
    >
      <ul className="space-y-2">
        {partyList.map((p, i) => {
          const role = p.roleKey || String(p.role || "").toLowerCase().replace(/'s? /g, "_").replace(/\s+/g, "_");
          const isSolicitor = role.includes("solicitor");
          const isSeller = role === "seller";
          const solSig = isSolicitor ? sigByRole.get(role) : null;
          return (
          <li
            key={i}
            className="group flex items-center gap-3 -mx-2 px-2 py-1.5 rounded-lg hover:bg-slate-50/70 transition-colors"
            data-testid={`party-${i}`}
          >
            <span
              className="w-9 h-9 rounded-full inline-flex items-center justify-center text-[10.5px] font-bold shrink-0 border"
              style={{ background: "#F8FAFC", color: "#475569", borderColor: "#E2E8F0" }}
            >
              {initials(p.name)}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[12.5px] font-semibold text-slate-900 truncate inline-flex items-center gap-2 flex-wrap">
                {p.name}
                {p.you && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: "#ECFDF5", color: "#047857" }}>You</span>}
                {isSeller && sellerSig && sellerSig.state !== "Stable" && (
                  <SellerConfidencePill
                    state={sellerSig.state}
                    reason={sellerSig.reasons?.[0] || null}
                    size="xs"
                    testid="dealroom-parties-seller-confidence"
                  />
                )}
                {isSolicitor && solSig && solSig.state !== "Responsive" && (
                  <SolicitorResponsivenessPill
                    state={solSig.state}
                    reason={solSig.reasons?.[0] || null}
                    size="xs"
                    testid={`dealroom-parties-${role}-responsiveness`}
                  />
                )}
              </p>
              <p className="text-[11px] text-slate-500 truncate">{p.role}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
              <button className="w-7 h-7 rounded-md inline-flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100" aria-label="Call" data-testid={`party-call-${i}`}>
                <Phone size={11} />
              </button>
              <button className="w-7 h-7 rounded-md inline-flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100" aria-label="Message" data-testid={`party-msg-${i}`}>
                <MessageSquare size={11} />
              </button>
            </div>
          </li>
          );
        })}
      </ul>
    </RailCard>
  );
}

function NextStepCard() {
  return (
    <RailCard
      title="Next Step"
      action={<ArrowRight size={13} className="text-slate-400" />}
      testid="rail-next-step"
      className=""
    >
      <div className="rounded-xl px-3 py-3" style={{ background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.18)" }}>
        <div className="flex items-start gap-2.5">
          <span className="w-8 h-8 rounded-lg inline-flex items-center justify-center shrink-0" style={{ background: "rgba(245,158,11,0.18)", color: "#B45309" }}>
            <ArrowRight size={13} className="-rotate-45" />
          </span>
          <div className="min-w-0">
            <p className="text-[13px] font-bold text-slate-900 leading-tight">Await signed enquiries</p>
            <p className="text-[11.5px] text-slate-600 mt-1 leading-relaxed">Buyer's solicitor to return signed enquiries.</p>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold mt-2.5" style={{ background: "#FFEDD5", color: "#C2410C" }}>
              Requires Attention
            </span>
          </div>
        </div>
      </div>
    </RailCard>
  );
}

function TransactionDocumentsCard({ documents, deal }) {
  // Calm operational doc set — when API empty we surface a deterministic leasehold-flavoured list.
  const docs = useMemo(() => {
    const fromApi = (documents || []).slice(0, 6).map((d, i) => ({
      id:   d.id || `doc-${i}`,
      name: d.name || d.title || "Document",
      date: d.uploaded_at || d.created_at,
      isNew: false,
      kind: classifyKind(d.folder || d.category || d.name),
    }));
    if (fromApi.length > 0) return fromApi;
    const refDate = deal?.targetExchange ? new Date(deal.targetExchange) : new Date();
    const d = (offsetDays) => new Date(refDate.getTime() - offsetDays * 86_400_000).toISOString();
    return [
      { id: "d1", name: "Draft Contract",            date: d(21), isNew: false, kind: "contract" },
      { id: "d2", name: "TA6 (Fittings & Contents)", date: d(33), isNew: false, kind: "document" },
      { id: "d3", name: "Leasehold Management Pack", date: d(22), isNew: true,  kind: "leasehold" },
      { id: "d4", name: "EPC",                       date: d(30), isNew: false, kind: "document" },
      { id: "d5", name: "Mortgage Offer (Indicative)", date: null, isNew: false, kind: "mortgage" },
    ];
  }, [documents, deal]);

  return (
    <RailCard
      title="Transaction Documents"
      action={<DocCountBadge />}
      testid="rail-documents"
    >
      <ul className="space-y-2">
        {docs.map((d) => {
          const tint = UPDATE_KIND_TINT[d.kind] || UPDATE_KIND_TINT.default;
          return (
            <li
              key={d.id}
              className="group flex items-center gap-2.5 -mx-2 px-2 py-1.5 rounded-lg hover:bg-slate-50/70 transition-colors"
              data-testid={`doc-${d.id}`}
            >
              <span
                className="w-8 h-8 rounded-lg inline-flex items-center justify-center shrink-0"
                style={{ background: tint.bg, color: tint.fg }}
              >
                <FileText size={12} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[12.5px] font-semibold text-slate-900 truncate">{d.name}</p>
                {d.isNew && (
                  <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-emerald-600">
                    New
                  </span>
                )}
              </div>
              <p className="text-[10.5px] text-slate-400 tabular-nums shrink-0">{fmtDateUK(d.date) || "—"}</p>
              <button
                className="w-6 h-6 rounded-md inline-flex items-center justify-center text-slate-300 hover:text-slate-700 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="More"
              >
                <MoreHorizontal size={12} />
              </button>
            </li>
          );
        })}
      </ul>
    </RailCard>
  );
}

function DocCountBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] font-bold text-slate-400 tabular-nums">
      <span className="text-slate-700">8</span> received
      <span className="text-slate-200">·</span>
      <span className="text-slate-700">2</span> outstanding
    </span>
  );
}

// ─── Log Update modal (structured operational update) ───────────

function LogUpdateModal({ open, onClose, onSubmit }) {
  const [kind, setKind] = useState("note");
  const [title, setTitle] = useState("");
  const [body, setBody]   = useState("");
  const [busy, setBusy]   = useState(false);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-40 flex items-end md:items-center justify-center px-4 pb-4 md:pb-0"
      style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(4px)" }}
      data-testid="log-update-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6"
        style={{ boxShadow: "0 24px 64px -16px rgba(15,23,42,0.30)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-[16px] font-bold text-slate-900">Log an update</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900" aria-label="Close">✕</button>
        </div>
        <p className="text-[12px] text-slate-500 mb-5">Record a calm operational update for this transaction.</p>

        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-400">Type</label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {[
                { id: "note",       label: "Note" },
                { id: "document",   label: "Document" },
                { id: "enquiries",  label: "Enquiries" },
                { id: "mortgage",   label: "Mortgage" },
                { id: "survey",     label: "Survey" },
                { id: "contract",   label: "Contract" },
              ].map((t) => {
                const active = kind === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setKind(t.id)}
                    className={`h-8 px-3 rounded-lg text-[11.5px] font-semibold ${active ? "text-white" : "text-slate-600 border border-slate-200 bg-white"}`}
                    style={active ? { background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)" } : undefined}
                    data-testid={`log-kind-${t.id}`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-400">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Mortgage valuation received"
              className="mt-1 w-full h-10 px-3 rounded-xl bg-white border border-slate-200 text-[13px] focus:outline-none focus:border-violet-300"
              data-testid="log-title"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-400">Detail</label>
            <textarea
              rows={3}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Concise operational summary…"
              className="mt-1 w-full px-3 py-2 rounded-xl bg-white border border-slate-200 text-[13px] focus:outline-none focus:border-violet-300 resize-none"
              data-testid="log-body"
            />
          </div>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-xl text-[12.5px] font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button
            type="button"
            disabled={busy || !title.trim()}
            onClick={async () => {
              if (!title.trim()) return;
              setBusy(true);
              try {
                await onSubmit({ activity_type: kind, title: title.trim(), body: body.trim() });
              } finally {
                setBusy(false);
              }
            }}
            className="h-10 px-4 rounded-xl text-[12.5px] font-semibold text-white"
            style={{
              background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
              opacity: busy || !title.trim() ? 0.6 : 1,
            }}
            data-testid="log-submit"
          >
            {busy ? "Logging…" : "Log update"}
          </button>
        </div>
      </div>
    </div>
  );
}
